import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  fetchBatchDetails,
  fetchBatchDetailsForView,
} from '@/services/tenant/share-credit-services';

export interface ShareCreditState {
  batchDetails: any;
  loadingBatchDetails: boolean;
  error: string | null;
}

const initialState: ShareCreditState = {
  batchDetails: null,
  loadingBatchDetails: false,
  error: null,
};

// Accepts either a plain batchNo (default details endpoint) or an object with a
// `mode`. When mode === 'review', the read-only "for view" endpoint is used.
type FetchBatchDetailsArg =
  | string
  | number
  | { batchNo: string | number; mode?: 'review' };

export const fetchBatchDetailsThunk = createAsyncThunk(
  'shareCredit/fetchBatchDetails',
  async (arg: FetchBatchDetailsArg, { rejectWithValue }) => {
    const batchNo = typeof arg === 'object' ? arg.batchNo : arg;
    const mode = typeof arg === 'object' ? arg.mode : undefined;
    try {
      const data =
        mode === 'review'
          ? await fetchBatchDetailsForView(batchNo)
          : await fetchBatchDetails(batchNo);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch batch details'
      );
    }
  }
);

const shareCreditSlice = createSlice({
  name: 'shareCredit',
  initialState,
  reducers: {
    clearBatchDetails(state) {
      state.batchDetails = null;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchBatchDetailsThunk.pending, state => {
        state.loadingBatchDetails = true;
        state.error = null;
      })
      .addCase(
        fetchBatchDetailsThunk.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingBatchDetails = false;
          state.batchDetails = action.payload;
        }
      )
      .addCase(fetchBatchDetailsThunk.rejected, (state, action: any) => {
        state.loadingBatchDetails = false;
        state.error = action.payload || 'Failed to fetch batch details';
      });
  },
});

export const { clearBatchDetails } = shareCreditSlice.actions;

export default shareCreditSlice.reducer;
